<script setup>
import { computed, ref } from "vue";
import fetchData from "@/utils/fetchData";
import StatCard from "./StatCard.vue";
import BarChartCard from "./BarChartCard.vue";
import PopularJourneys from "./PopularJourneys.vue";
import { count, money } from "@/utils/format";

const OUTPUT_NAMES = {
  text: "Text",
  docx: "Word",
  pdf: "PDF",
  xlsx: "Spreadsheet",
  pptx: "Slides",
  image: "Picture",
  unknown: "Before formats",
};

const features = ref(null);
fetchData("getFeatureData").then((data) => (features.value = data));

const days = computed(() => features.value?.days || 30);

const outputs = computed(() =>
  (features.value?.outputs || []).map((row) => ({
    x: OUTPUT_NAMES[row.x] || row.x,
    y: row.y,
  }))
);

const revenue = computed(() => {
  const rows = features.value?.wishes?.revenue || [];
  if (!rows.length) return "£0";
  return rows.map((row) => money(row.amount, row.currency)).join(" + ");
});

const cards = computed(() => {
  const data = features.value;
  if (!data) return [];
  return [
    { title: "Researched makes", total: count(data.research) },
    { title: "Pictures drawn", total: count(data.pictures) },
    { title: "Edits", total: count(data.edits) },
    {
      title: "Documents and links",
      total: count(data.documents + data.links),
      extraText: `${count(data.documents)} documents, ${count(
        data.links
      )} links`,
    },
    {
      title: "Saved makes",
      total: count(data.savedMakes),
      extraText: `All time, ${count(data.sharedMakes)} shared`,
    },
    {
      title: "Wishes spent",
      total: count(data.wishes.spent - data.wishes.refunded),
      extraText: `${count(data.wishes.refunded)} refunded after failures`,
    },
    {
      title: "Wishes bought",
      total: count(data.wishes.bought),
      extraText: `${count(data.wishes.purchases)} purchases`,
    },
    { title: "Wish sales", total: revenue.value },
  ];
});
</script>

<template>
  <section class="container mx-auto px-4 py-3">
    <h2 class="section-title mb-3">
      Features
      <span class="text-sm font-normal text-secondary dark:text-night-muted">
        in the last {{ days }} days
      </span>
    </h2>
    <div class="grid gap-4 lg:grid-cols-2">
      <BarChartCard
        title="Makes by format"
        :note="`Last ${days} days`"
        :rows="outputs"
      />
      <PopularJourneys />
    </div>
    <div class="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard
        v-for="card in cards"
        :key="card.title"
        :title="card.title"
        :total="card.total"
        :extra-text="card.extraText"
      />
    </div>
  </section>
</template>
